<template>
	<div class="fp-detail">
		<div v-if="intestazione" class="fp-head">
			<v-avatar size="20px" tile class="mr-1">
				<img :src="require('@/assets/images/communication/firebase.svg')" alt="Firebase" />
			</v-avatar>
			<span class="fp-head-title">Configurazione Firebase Push Notifications</span>
			<span class="fp-muted ml-2">{{ modello.active ? 'attivo' : 'disattivato' }}</span>
		</div>

		<div class="fp-body">
			<v-alert outlined text dense type="info" class="text-body-2 mb-4">Configura Firebase Cloud Messaging per inviare notifiche push su Web (Nuxt3), iOS e Android.</v-alert>

			<div class="fp-section">Credenziali</div>
			<div class="fp-grid mb-4">
				<div class="fp-full">
					<v-file-input
						v-model="fileCredenziali"
						label="File JSON con le credenziali Firebase (Service Account)"
						accept=".json"
						show-size
						dense
						outlined
						hide-details
						background-color="white"
						@change="$emit('upload', $event)"
					/>
					<a v-if="setting.credentials_url" :href="setting.credentials_url" target="_blank" class="fp-small">File JSON caricato</a>
				</div>
				<v-text-field v-model="setting.project_id" label="Project ID" dense outlined hide-details background-color="white" />
				<v-text-field v-model="setting.client_email" label="Client Email del service account" dense outlined hide-details background-color="white">
					<template #prepend-inner>
						<v-icon small :color="setting.client_email ? 'success' : 'grey'">{{ setting.client_email ? 'mdi-check-circle' : 'mdi-email' }}</v-icon>
					</template>
				</v-text-field>
				<v-text-field
					v-model="setting.private_key"
					:type="mostraChiave ? 'text' : 'password'"
					:append-icon="mostraChiave ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
					label="Private Key del service account"
					autocomplete="new-password"
					dense
					outlined
					hide-details
					background-color="white"
					class="fp-full"
					@click:append="mostraChiave = !mostraChiave"
				>
					<template #prepend-inner>
						<v-icon small :color="setting.private_key ? 'success' : 'grey'">{{ setting.private_key ? 'mdi-check-circle' : 'mdi-key' }}</v-icon>
					</template>
				</v-text-field>
				<v-textarea
					v-model="setting.vapid_key"
					label="Web Push Certificate (VAPID Key) — chiave pubblica per le notifiche web"
					rows="2"
					auto-grow
					dense
					outlined
					hide-details
					background-color="white"
					class="fp-full"
				/>
			</div>

			<div class="fp-section">Cosa inviare</div>
			<div class="fp-grid mb-4">
				<v-select
					v-model="setting.channels"
					:items="availableChannels"
					item-text="label"
					item-value="value"
					label="Canali attivi"
					multiple
					dense
					outlined
					hide-details
					background-color="white"
				>
					<template #selection="{ item }">
						<v-chip small class="my-1">
							<v-icon left small>{{ item.icon }}</v-icon>
							{{ item.label }}
						</v-chip>
					</template>
					<template #item="{ item }">
						<v-icon left>{{ item.icon }}</v-icon>
						{{ item.label }}
					</template>
				</v-select>
				<v-select
					v-model="setting.notification_types"
					:items="notificationTypes"
					item-text="label"
					item-value="value"
					label="Eventi che generano una notifica"
					multiple
					dense
					outlined
					hide-details
					background-color="white"
				>
					<template #selection="{ item }">
						<v-chip small :color="item.color" text-color="white" class="my-1">
							<v-icon left small>{{ item.icon }}</v-icon>
							{{ item.label }}
						</v-chip>
					</template>
					<template #item="{ item }">
						<v-list-item-icon>
							<v-icon :color="item.color">{{ item.icon }}</v-icon>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title>{{ item.label }}</v-list-item-title>
							<v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
						</v-list-item-content>
					</template>
				</v-select>
			</div>

			<div class="fp-section">Piattaforme e prova</div>
			<div class="fp-tabs-box">
				<v-tabs v-model="tab" show-arrows height="38" background-color="transparent" class="fp-tabs">
					<v-tab v-for="t in tabs" :key="t.key" class="px-3">
						<v-icon left small :color="t.colore">{{ t.icona }}</v-icon>
						{{ t.nome }}
					</v-tab>
				</v-tabs>
				<div class="fp-tab-body">
					<!-- Web -->
					<template v-if="tabCorrente === 'web'">
						<div class="fp-muted fp-small mb-3">
							Su <a href="https://console.firebase.google.com" target="_blank">Firebase Console</a>: seleziona il progetto, Impostazioni progetto &gt; Le tue app, scegli o aggiungi un'app Web e copia i valori della configurazione.
						</div>
						<div class="fp-grid">
							<v-text-field v-model="setting.web.api_key" label="API Key *" prepend-inner-icon="mdi-key" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.web.auth_domain" label="Auth Domain *" prepend-inner-icon="mdi-web" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.web.storage_bucket" label="Storage Bucket" prepend-inner-icon="mdi-database" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.sender_id" label="Messaging Sender ID *" prepend-inner-icon="mdi-email-send" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.web.app_id" label="App ID *" prepend-inner-icon="mdi-application" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.web.measurement_id" label="Measurement ID (opzionale)" prepend-inner-icon="mdi-chart-line" dense outlined hide-details background-color="white" />
							<v-text-field v-model="setting.web.app_url" label="URL applicazione web (es. https://shop.tuosito.com)" prepend-inner-icon="mdi-link" dense outlined hide-details background-color="white" class="fp-full" />
							<v-text-field v-model="setting.web.icon_url" label="URL icona della notifica" prepend-inner-icon="mdi-image" dense outlined hide-details background-color="white" class="fp-full" />
						</div>
					</template>
					<!-- iOS -->
					<div v-else-if="tabCorrente === 'ios'" class="fp-grid">
						<v-text-field v-model="setting.ios.bundle_id" label="Bundle ID (es. com.azseller.app)" dense outlined hide-details background-color="white" />
						<v-text-field v-model="setting.ios.team_id" label="Apple Developer Team ID" dense outlined hide-details background-color="white" />
					</div>
					<!-- Android -->
					<div v-else-if="tabCorrente === 'android'" class="fp-grid">
						<v-text-field v-model="setting.android.package_name" label="Package Name (es. com.azseller.app)" dense outlined hide-details background-color="white" />
						<v-select v-model="setting.android.priority" :items="['high', 'normal']" label="Priorità notifiche" dense outlined hide-details background-color="white" />
					</div>
					<!-- Prova invio -->
					<template v-else-if="tabCorrente === 'test'">
						<div class="fp-muted fp-small mb-3">Manda una notifica vera al dispositivo indicato, con la configurazione salvata.</div>
						<div class="fp-grid">
							<v-text-field v-model="token" label="Token FCM del dispositivo di test" dense outlined hide-details background-color="white" class="fp-full" />
							<v-textarea v-model="messaggio" label="Messaggio di test" rows="2" auto-grow dense outlined hide-details background-color="white" class="fp-full" />
						</div>
						<v-btn small depressed color="info" class="mt-3" :disabled="!token || !messaggio" @click="$emit('test')">
							<v-icon left small>mdi-send</v-icon>
							Invia notifica di test
						</v-btn>
					</template>
				</div>
			</div>
		</div>

		<div class="fp-foot">
			<v-btn small outlined color="primary" @click="$emit('check')"><v-icon left small>mdi-connection</v-icon>Verifica connessione</v-btn>
			<v-spacer></v-spacer>
			<v-btn depressed small color="primary" @click="$emit('save')"><v-icon left small>mdi-content-save</v-icon>Salva configurazione</v-btn>
		</div>
	</div>
</template>

<script>
// riquadro di configurazione di Firebase Push: riga espansa (desktop) e card (mobile).
// I campi scrivono direttamente in modello.setting; upload, salvataggio e invio di prova li fa la pagina.
export default {
	name: 'FirebasePushPanel',
	props: {
		modello: { type: Object, required: true },
		availableChannels: { type: Array, default: () => [] },
		notificationTypes: { type: Array, default: () => [] },
		// token e messaggio della prova vivono nella pagina (li usa sendTestNotification): .sync
		testToken: { type: String, default: '' },
		testMessage: { type: String, default: '' },
		intestazione: { type: Boolean, default: true },
	},
	data() {
		return {
			tab: 0,
			fileCredenziali: null,
			mostraChiave: false,
		};
	},
	computed: {
		setting() {
			return this.modello.setting || {};
		},
		canali() {
			return this.setting.channels || [];
		},
		// tab di piattaforma solo per i canali attivi, come i pannelli di prima
		tabs() {
			const lista = [];
			if (this.canali.includes('web')) lista.push({ key: 'web', nome: 'Web', icona: 'mdi-firebase', colore: 'orange' });
			if (this.canali.includes('ios')) lista.push({ key: 'ios', nome: 'iOS', icona: 'mdi-apple' });
			if (this.canali.includes('android')) lista.push({ key: 'android', nome: 'Android', icona: 'mdi-android', colore: 'green' });
			lista.push({ key: 'test', nome: 'Prova invio', icona: 'mdi-test-tube' });
			return lista;
		},
		tabCorrente() {
			const t = this.tabs[this.tab] || this.tabs[0];
			return t.key;
		},
		token: {
			get() {
				return this.testToken;
			},
			set(v) {
				this.$emit('update:testToken', v);
			},
		},
		messaggio: {
			get() {
				return this.testMessage;
			},
			set(v) {
				this.$emit('update:testMessage', v);
			},
		},
	},
	watch: {
		// se un canale viene tolto la tab selezionata puo' sparire: si torna alla prima
		tabs(nuove) {
			if (this.tab >= nuove.length) this.tab = 0;
		},
	},
};
</script>

<style scoped>
.fp-detail {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	width: 100%;
}
.fp-head {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #e1e7ef;
	font-size: 13px;
}
.fp-head-title {
	font-weight: 600;
	color: #1f2d3d;
}
.fp-body {
	padding: 12px;
}
.fp-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.fp-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px 16px;
}
.fp-full {
	grid-column: 1 / -1;
}
.fp-muted {
	color: #5f6b7a;
}
.fp-small {
	font-size: 13px;
}
.fp-tabs-box {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.fp-tabs {
	border-bottom: 1px solid #eef2f7;
}
.fp-tabs ::v-deep .v-tab {
	min-width: 0;
	font-size: 12px;
	letter-spacing: 0.03em;
}
.fp-tab-body {
	padding: 12px;
}
.fp-foot {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 8px 12px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
}
@media (max-width: 768px) {
	.fp-body {
		padding: 8px;
	}
	.fp-grid {
		grid-template-columns: 1fr;
	}
}
</style>
